import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, CameraOff, Mic, MicOff, Video, VideoOff, Wifi, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { IVSBroadcaster } from '@/utils/ivs/broadcaster';

interface VideoPreviewProps {
  isStreamActive?: boolean;
  streamId?: string;
  enableBroadcast?: boolean;
  shouldStopCamera?: boolean;
  onRecordingComplete?: (blob: Blob, duration: number) => void;
}

export const VideoPreview = ({ 
  isStreamActive = false,
  streamId,
  enableBroadcast = false,
  shouldStopCamera = false,
  onRecordingComplete
}: VideoPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const broadcasterRef = useRef<IVSBroadcaster | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingStartTimeRef = useRef<number>(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<string>('none');
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [cameraStatus, setCameraStatus] = useState<'starting' | 'active' | 'stopped' | 'error'>('stopped');

  const filters = [
    { id: 'none', name: 'None', class: '' },
    { id: 'grayscale', name: 'B&W', class: 'grayscale' },
    { id: 'sepia', name: 'Vintage', class: 'sepia' },
    { id: 'brightness', name: 'Bright', class: 'brightness-125' },
    { id: 'contrast', name: 'Pop', class: 'contrast-125' },
  ];

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (enableBroadcast && streamId && stream && !isBroadcasting) {
      console.log('Attempting to start broadcast...', { enableBroadcast, streamId, hasStream: !!stream });
      startBroadcast(stream);
      startRecording();
    } else if (!enableBroadcast && isBroadcasting) {
      console.log('Stopping broadcast...');
      stopBroadcast();
      stopRecording();
    }
  }, [enableBroadcast, streamId, stream]);

  useEffect(() => {
    if (shouldStopCamera) {
      stopCamera();
    }
  }, [shouldStopCamera]);

  const startCamera = async () => {
    setCameraStatus('starting');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        try {
          await videoRef.current.play();
        } catch (e) {
          console.warn('Autoplay may be blocked', e);
        }
      }
      setStream(mediaStream);
      setCameraStatus('active');
      toast.success('Camera ready');

    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraStatus('error');
      toast.error('Failed to access camera/microphone. Please check permissions.');
    }
  };

  const startBroadcast = async (mediaStream: MediaStream) => {
    if (!streamId || isBroadcasting) {
      console.log('Cannot start broadcast:', { streamId, isBroadcasting });
      return;
    }
    
    try {
      console.log('Starting AWS IVS broadcast...', { streamId });

      broadcasterRef.current = new IVSBroadcaster(streamId, streamId);
      
      await broadcasterRef.current.initialize(mediaStream);
      
      setIsBroadcasting(true);
      toast.success('🔴 Broadcasting live with AWS IVS');
      console.log('Broadcast started successfully');
    } catch (error: any) {
      console.error('Failed to start broadcast:', error);
      toast.error(`Broadcast failed: ${error.message || 'Unknown error'}`);
      setIsBroadcasting(false);
      broadcasterRef.current = null;
    }
  };

  const stopBroadcast = async () => {
    if (isBroadcasting && broadcasterRef.current) {
      await broadcasterRef.current.stopBroadcast();
      broadcasterRef.current = null;
      setIsBroadcasting(false);
      setViewerCount(0);
    }
  };

  const startRecording = () => {
    if (!stream || isRecording) return;

    try {
      const options = { mimeType: 'video/webm;codecs=vp8,opus' };
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      recordedChunksRef.current = [];
      recordingStartTimeRef.current = Date.now();

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const duration = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        if (onRecordingComplete) {
          onRecordingComplete(blob, duration);
        }
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      console.log('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      console.log('Recording stopped');
    }
  };

  const stopCamera = async () => {
    await stopBroadcast();
    stopRecording();

    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      setStream(null);
      setCameraStatus('stopped');
    }
  };

  const toggleVideo = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };


  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
          {/* Camera Status Indicator */}
          {cameraStatus === 'starting' && (
            <div className="absolute top-2 right-2 z-10">
              <div className="flex items-center gap-2 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                Starting camera...
              </div>
            </div>
          )}
          {cameraStatus === 'active' && (
            <div className="absolute top-2 right-2 z-10 flex gap-2">
              <div className="flex items-center gap-2 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                Camera active
              </div>
              {isBroadcasting && (
                <div className="flex items-center gap-2 bg-red-600/90 text-white px-3 py-1.5 rounded-full text-xs">
                  <Wifi className="w-3 h-3 animate-pulse" />
                  LIVE • {viewerCount} viewers
                </div>
              )}
              {isRecording && (
                <div className="flex items-center gap-2 bg-red-600/90 text-white px-3 py-1.5 rounded-full text-xs">
                  <Circle className="w-3 h-3 fill-white animate-pulse" />
                  REC
                </div>
              )}
            </div>
          )}
          {cameraStatus === 'error' && (
            <div className="absolute top-2 right-2 z-10">
              <div className="flex items-center gap-2 bg-destructive text-white px-3 py-1.5 rounded-full text-xs">
                <div className="w-2 h-2 bg-white rounded-full" />
                Camera error
              </div>
            </div>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${filters.find(f => f.id === selectedFilter)?.class || ''}`}
          />
          {(!isVideoOn || cameraStatus !== 'active') && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <CameraOff className="w-16 h-16 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Filter Selection */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {filters.map((filter) => (
            <Button
              key={filter.id}
              variant={selectedFilter === filter.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedFilter(filter.id)}
              className="flex-shrink-0"
            >
              {filter.name}
            </Button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex gap-2 justify-center">
          <Button
            variant={isVideoOn ? 'secondary' : 'destructive'}
            size="icon"
            onClick={toggleVideo}
            disabled={cameraStatus !== 'active'}
            title={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {isVideoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </Button>
          <Button
            variant={isAudioOn ? 'secondary' : 'destructive'}
            size="icon"
            onClick={toggleAudio}
            disabled={cameraStatus !== 'active'}
            title={isAudioOn ? 'Mute microphone' : 'Unmute microphone'}
          >
            {isAudioOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </Button>
          {/* Restart Camera Button */}
          {cameraStatus === 'stopped' && (
            <Button
              variant="outline"
              size="sm"
              onClick={startCamera}
              className="ml-2"
            >
              <Camera className="h-4 w-4 mr-2" />
              Restart Camera
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};